<template>
  <div class="flex h-screen bg-gray-50">
    <!-- Sidebar -->
    <SideBar class="flex-shrink-0" />
    
    <!-- Main content area -->
    <div class="flex-1 overflow-auto relative">
      <div class="p-4 md:p-6 h-full">
        <!-- Header with title, search and add button -->
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">Leads</h1>
            <p class="text-gray-500 mt-1">Manage and track your sales leads</p>
          </div>
          <div class="flex items-center gap-3">
            <!-- Smaller search input in top right -->
            <div class="relative">
              <span class="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400">🔍</span>
              <input 
                v-model="searchQuery" 
                type="text" 
                placeholder="Search..." 
                class="pl-8 pr-3 py-1.5 w-40 sm:w-48 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              />
            </div>
            <select 
              v-model="statusFilter" 
              class="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
            >
              <option value="">All Statuses</option>
              <option v-for="status,index in statusesList" :value="status" :key="index">{{ status }}</option>
            </select>
            <select 
              v-model="stateFilter" 
              class="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
            >
              <option value="">All States</option>
              <option v-for="state in availableStates" :value="state" :key="state">{{ state }}</option>
            </select>
            <button @click="openAddModal" class="px-4 py-2 bg-primary text-black rounded-lg shadow-sm hover:bg-primary/90 transition-colors flex items-center gap-2">
              <span class="text-sm">+</span>
              <span>Add Lead</span>
            </button>
          </div>
        </div>
    
        <!-- Follow-up filter options -->
        <div class="flex flex-wrap gap-3 mb-4">
          <button 
            @click="followUpFilter = ''" 
            :class="[
              'px-3 py-1.5 text-sm rounded-md border transition-colors',
              followUpFilter === '' 
                ? 'bg-primary text-black border-primary' 
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            ]"
          >
            All Leads
          </button>
          <button 
            @click="followUpFilter = 'today'" 
            :class="[
              'px-3 py-1.5 text-sm rounded-md border transition-colors',
              followUpFilter === 'today' 
                ? 'bg-primary text-black border-primary' 
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            ]"
          >
            <span class="flex items-center gap-1">
              <span class="h-2 w-2 rounded-full bg-red-500"></span>
              Follow-up Today
            </span>
          </button>
          <button 
            @click="followUpFilter = 'overdue'" 
            :class="[
              'px-3 py-1.5 text-sm rounded-md border transition-colors',
              followUpFilter === 'overdue' 
                ? 'bg-primary text-black border-primary' 
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            ]"
          >
            <span class="flex items-center gap-1">
              <span class="h-2 w-2 rounded-full bg-red-600"></span>
              Overdue Follow-ups
            </span>
          </button>
          <button 
            @click="followUpFilter = 'upcoming'" 
            :class="[
              'px-3 py-1.5 text-sm rounded-md border transition-colors',
              followUpFilter === 'upcoming' 
                ? 'bg-primary text-black border-primary' 
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            ]"
          >
            <span class="flex items-center gap-1">
              <span class="h-2 w-2 rounded-full bg-amber-400"></span>
              Upcoming (7 days)
            </span>
          </button>
          <button 
            @click="followUpFilter = 'scheduled'" 
            :class="[
              'px-3 py-1.5 text-sm rounded-md border transition-colors',
              followUpFilter === 'scheduled' 
                ? 'bg-primary text-black border-primary' 
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            ]"
          >
            <span class="flex items-center gap-1">
              <span class="h-2 w-2 rounded-full bg-green-500"></span>
              All Scheduled
            </span>
          </button>
          <button 
            @click="followUpFilter = 'none'" 
            :class="[
              'px-3 py-1.5 text-sm rounded-md border transition-colors',
              followUpFilter === 'none' 
                ? 'bg-primary text-black border-primary' 
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            ]"
          >
            No Follow-up Set
          </button>
        </div>
      
        <!-- Table with flex-1 to take remaining height -->
        <div class="overflow-hidden rounded-lg border border-gray-200 bg-white flex flex-col h-[calc(100vh-200px)]">
          <div class="overflow-x-auto flex-1">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th 
                    v-for="column in columns" 
                    :key="column.key" 
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    @click="handleSort(column.key)"
                  >
                    <div class="flex items-center gap-1">
                      {{ column.label }}
                      <div v-if="sortBy === column.key" class="flex flex-col">
                        <span v-if="sortDirection === 'asc'">▲</span>
                        <span v-else>▼</span>
                      </div>
                    </div>
                  </th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200 overflow-visible">
                <tr 
                  v-for="lead in paginatedLeads" 
                  :key="lead.id" 
                  class="hover:bg-gray-50 transition-colors cursor-pointer  overflow-visible"
                  :class="{'bg-red-50': isOverdue(lead.follow_up_date), 'bg-amber-50': isToday(lead.follow_up_date)}"
                  @click="openCommentsSlider(lead)"
                >
                <td class="px-6 py-4 whitespace-nowrap overflow-visible">
                <div class="flex items-center overflow-visible">
                  <div class="h-10 w-10 flex-shrink-0">
                    <img
                      class="h-10 w-10 rounded-full object-cover"
                      :src="JSON.parse(lead.meta).thumbnail"
                      alt=""
                    />
                  </div>
                  <div class="ml-4 max-w-[500px]">
                    <div class="text-sm font-medium text-gray-900" :title="lead.name">
                      {{ lead.name }}
                      <div class="relative inline-block group overflow-visible">
                        <span
                          class="h-3 w-3 rounded-full inline-block"
                          :class="isOpenNowFancy(getTodayHours(lead.meta), todayName) ? 'bg-green-500' : 'bg-red-500'"
                          :title="getTodayHours(lead.meta) ? `Open: ${getTodayHours(lead.meta)}` : 'Closed today'"
                        ></span>
                        <div
                          class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-white border border-gray-200 rounded p-2 text-xs shadow-lg z-50"
                        >
                          <template v-for="({ day, hours }) in parseHours(lead.meta)" :key="day">
                            <div  :class="[
                            'whitespace-nowrap',
                            day.toLowerCase() === todayName.toLowerCase()
                              ? 'font-semibold text-black'
                              : 'text-gray-700'
                          ]">
                          {{ day }}: {{ hours }}
                          </div>
                          </template>
                        </div>
                      </div>
                    </div>
                    <div class="text-sm text-gray-500 truncate" :title="lead.contact_person">
                      {{ lead.contact_person }}
                    </div>
                    <div class="text-sm text-gray-400 truncate" :title="lead.street">
                      {{ lead.street }} {{ lead.city }} {{ lead.state }}, {{ lead.zip }}
                    </div>
                  </div>
                </div>
              </td>

                  <td class="px-6 py-4 whitespace-nowrap">
                    <div
                      class="inline-flex items-center justify-center w-8 h-8 rounded-full text-black text-sm font-bold"
                      :style="{ backgroundColor: getAvatarColor(lead.user_id) }"
                      :title="$store.getters['leads/usernameById'](lead.user_id)"
                    >
                      {{ getInitials($store.getters['leads/usernameById'](lead.user_id)) }}
                    </div>
                </td>
                  <td class="px-6 py-4 whitespace-nowrap" @click.stop>
                    <div class="relative inline-block">
                      <!-- view mode -->
                      <button
                        v-if="statusDropdownOpen !== lead.id"
                        @click="openStatusDropdown(lead)"
                        :class="['px-2 text-xs font-semibold rounded-full', getStatusClass(lead.status)]"
                      >
                        {{ lead.status }}
                      </button>
  
                      <!-- edit mode -->
                      <div
                        v-else
                        class="absolute left-0 mt-1 w-36 bg-white border border-gray-200 rounded shadow-sm p-2 z-20"
                      >
                        <select
                          v-model="statusDraft"
                          class="w-full text-sm py-1 px-2 border border-gray-200 rounded focus:outline-none"
                        >
                          <option v-for="s in statusesList" :key="s" :value="s">{{ s }}</option>
                        </select>
                        <div class="mt-2 flex justify-end gap-2">
                          <button
                            @click="statusDropdownOpen = null"
                            class="px-2 py-1 text-sm text-gray-500 rounded hover:bg-gray-100"
                          >
                            Cancel
                          </button>
                          <button @click="updateLead(lead, { status: statusDraft })">Save</button>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap" @click.stop>
                    <button
                      class="cursor-pointer px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 hover:bg-gray-200"
                      @click="copyPhone(lead.phone, lead.id)"
                    >
                      <span v-if="copiedId === lead.id" class="text-green-600">
                        Copied!
                      </span>
                      <span v-else>
                        {{ lead.phone }}
                      </span>
                    </button>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm">
                    <div class="flex items-center gap-2">
                      <!-- Follow-up date indicator -->
                      <span 
                        v-if="lead.follow_up_date" 
                        :class="[
                          'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium',
                          isOverdue(lead.follow_up_date) ? 'bg-red-100 text-red-800' : 
                          isToday(lead.follow_up_date) ? 'bg-amber-100 text-amber-800' :
                          isUpcoming(lead.follow_up_date) ? 'bg-amber-50 text-amber-700' :
                          'bg-green-50 text-green-700'
                        ]"
                      >
                        <span 
                          class="h-2 w-2 rounded-full" 
                          :class="[
                            isOverdue(lead.follow_up_date) ? 'bg-red-600' : 
                            isToday(lead.follow_up_date) ? 'bg-red-500' :
                            isUpcoming(lead.follow_up_date) ? 'bg-amber-400' :
                            'bg-green-500'
                          ]"
                        ></span>
                        {{ formatFollowUpDate(lead.follow_up_date) }}
                      </span>
                      <span v-else class="text-gray-400 text-xs">No follow-up</span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap flex justify-center text-sm font-medium" @click.stop>
                    <svg @click="openEditModal(lead)" class="cursor-pointer" style="width: 30px;" viewBox="0 -0.5 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M13.2942 7.95881C13.5533 7.63559 13.5013 7.16358 13.178 6.90453C12.8548 6.64549 12.3828 6.6975 12.1238 7.02072L13.2942 7.95881ZM6.811 14.8488L7.37903 15.3385C7.38489 15.3317 7.39062 15.3248 7.39623 15.3178L6.811 14.8488ZM6.64 15.2668L5.89146 15.2179L5.8908 15.2321L6.64 15.2668ZM6.5 18.2898L5.7508 18.2551C5.74908 18.2923 5.75013 18.3296 5.75396 18.3667L6.5 18.2898ZM7.287 18.9768L7.31152 19.7264C7.36154 19.7247 7.41126 19.7181 7.45996 19.7065L7.287 18.9768ZM10.287 18.2658L10.46 18.9956L10.4716 18.9927L10.287 18.2658ZM10.672 18.0218L11.2506 18.4991L11.2571 18.491L10.672 18.0218ZM17.2971 10.959C17.5562 10.6358 17.5043 10.1638 17.1812 9.90466C16.8581 9.64552 16.386 9.69742 16.1269 10.0206L17.2971 10.959ZM12.1269 7.02052C11.8678 7.34365 11.9196 7.81568 12.2428 8.07484C12.5659 8.33399 13.0379 8.28213 13.2971 7.95901L12.1269 7.02052ZM14.3 5.50976L14.8851 5.97901C14.8949 5.96672 14.9044 5.95412 14.9135 5.94123L14.3 5.50976ZM15.929 5.18976L16.4088 4.61332C16.3849 4.59344 16.3598 4.57507 16.3337 4.5583L15.929 5.18976ZM18.166 7.05176L18.6968 6.52192C18.6805 6.50561 18.6635 6.49007 18.6458 6.47532L18.166 7.05176ZM18.5029 7.87264L19.2529 7.87676V7.87676L18.5029 7.87264ZM18.157 8.68976L17.632 8.15412C17.6108 8.17496 17.5908 8.19704 17.5721 8.22025L18.157 8.68976ZM16.1271 10.0203C15.8678 10.3433 15.9195 10.8153 16.2425 11.0746C16.5655 11.3339 17.0376 11.2823 17.2969 10.9593L16.1271 10.0203ZM13.4537 7.37862C13.3923 6.96898 13.0105 6.68666 12.6009 6.74805C12.1912 6.80943 11.9089 7.19127 11.9703 7.60091L13.4537 7.37862ZM16.813 11.2329C17.2234 11.1772 17.5109 10.7992 17.4552 10.3888C17.3994 9.97834 17.0215 9.69082 16.611 9.74659L16.813 11.2329ZM12.1238 7.02072L6.22577 14.3797L7.39623 15.3178L13.2942 7.95881L12.1238 7.02072ZM6.24297 14.359C6.03561 14.5995 5.91226 14.9011 5.89159 15.218L7.38841 15.3156C7.38786 15.324 7.38457 15.3321 7.37903 15.3385L6.24297 14.359ZM5.8908 15.2321L5.7508 18.2551L7.2492 18.3245L7.3892 15.3015L5.8908 15.2321ZM5.75396 18.3667C5.83563 19.1586 6.51588 19.7524 7.31152 19.7264L7.26248 18.2272C7.25928 18.2273 7.25771 18.2268 7.25669 18.2264C7.25526 18.2259 7.25337 18.2249 7.25144 18.2232C7.2495 18.2215 7.24825 18.2198 7.24754 18.2185C7.24703 18.2175 7.24637 18.216 7.24604 18.2128L5.75396 18.3667ZM7.45996 19.7065L10.46 18.9955L10.114 17.536L7.11404 18.247L7.45996 19.7065ZM10.4716 18.9927C10.7771 18.9151 11.05 18.7422 11.2506 18.499L10.0934 17.5445C10.0958 17.5417 10.0989 17.5397 10.1024 17.5388L10.4716 18.9927ZM11.2571 18.491L17.2971 10.959L16.1269 10.0206L10.0869 17.5526L11.2571 18.491ZM13.2971 7.95901L14.8851 5.97901L13.7149 5.04052L12.1269 7.02052L13.2971 7.95901ZM14.9135 5.94123C15.0521 5.74411 15.3214 5.6912 15.5243 5.82123L16.3337 4.5583C15.4544 3.99484 14.2873 4.2241 13.6865 5.0783L14.9135 5.94123ZM15.4492 5.7662L17.6862 7.6282L18.6458 6.47532L16.4088 4.61332L15.4492 5.7662ZM17.6352 7.58161C17.7111 7.6577 17.7535 7.761 17.7529 7.86852L19.2529 7.87676C19.2557 7.36905 19.0555 6.88127 18.6968 6.52192L17.6352 7.58161ZM17.7529 7.86852C17.7524 7.97604 17.7088 8.07886 17.632 8.15412L18.682 9.22541C19.0446 8.87002 19.2501 8.38447 19.2529 7.87676L17.7529 7.86852ZM17.5721 8.22025L16.1271 10.0203L17.2969 10.9593L18.7419 9.15928L17.5721 8.22025ZM11.9703 7.60091C12.3196 9.93221 14.4771 11.5503 16.813 11.2329L16.611 9.74659C15.0881 9.95352 13.6815 8.89855 13.4537 7.37862L11.9703 7.60091Z" fill="#5c5c5c"></path> </g></svg>
                    <svg @click.stop="openEmailSlider(lead)" class="cursor-pointer ml-2" style="width: 25px;" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 7.00005L10.2 11.65C11.2667 12.45 12.7333 12.45 13.8 11.65L20 7" stroke="#5c5c5c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="#5c5c5c" strokeWidth="2" strokeLinecap="round"/>
    </svg>
                  </td>
                </tr>
                <tr v-if="filteredLeads.length === 0">
                  <td colspan="6" class="px-6 py-10 text-center text-gray-500">
                    No leads found matching your search criteria
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Pagination -->
          <div class="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
            <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p class="text-sm text-gray-700">
                  Showing
                  <span class="font-medium">{{ startIndex + 1 }}</span>
                  to
                  <span class="font-medium">{{ Math.min(endIndex, filteredLeads.length) }}</span>
                  of
                  <span class="font-medium">{{ filteredLeads.length }}</span>
                  results
                </p>
              </div>
              <div>
                <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    @click="currentPage = 1"
                    :disabled="currentPage === 1"
                    class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span class="sr-only">First</span>
                    ⟪
                  </button>
                  <button
                    @click="currentPage--"
                    :disabled="currentPage === 1"
                    class="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span class="sr-only">Previous</span>
                    ◀
                  </button>
                  
                  <!-- Page numbers -->
                  <template v-for="page in displayedPageNumbers" :key="page">
                    <button
                      v-if="page !== '...'"
                      @click="currentPage = page"
                      :class="[
                        'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                        currentPage === page
                          ? 'z-10 bg-primary border-primary text-black'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      ]"
                    >
                      {{ page }}
                    </button>
                    <span
                      v-else
                      class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                    >
                      ...
                    </span>
                  </template>
                  
                  <button
                    @click="currentPage++"
                    :disabled="currentPage === totalPages"
                    class="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span class="sr-only">Next</span>
                    ▶
                  </button>
                  <button
                    @click="currentPage = totalPages"
                    :disabled="currentPage === totalPages"
                    class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span class="sr-only">Last</span>
                    ⟫
                  </button>
                </nav>
              </div>
            </div>
            
            <!-- Mobile pagination -->
            <div class="flex items-center justify-between w-full sm:hidden">
              <button
                @click="currentPage--"
                :disabled="currentPage === 1"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <div class="text-sm text-gray-500">
                Page {{ currentPage }} of {{ totalPages }}
              </div>
              <button
                @click="currentPage++"
                :disabled="currentPage === totalPages"
                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Comments Slider -->
      <div 
        v-if="commentsSliderOpen" 
        class="fixed inset-y-0 right-0 w-full sm:w-1/3 bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-30 overflow-hidden flex flex-col"
        :class="{ 'translate-x-0': commentsSliderOpen, 'translate-x-full': !commentsSliderOpen }"
      >
        <div class="flex items-center justify-between p-4 border-b">
          <div>
            <h3 class="text-lg font-semibold">{{ activeLead?.name }}</h3>
            <p class="text-sm text-gray-500">{{ activeLead?.company }}</p>
          </div>
          <button 
            @click="closeCommentsSlider" 
            class="p-2 rounded-full hover:bg-gray-100"
          >
            <span class="text-xl">×</span>
          </button>
        </div>
        
        <div class="flex-1 overflow-y-auto p-4">
          <!-- Follow-up date section -->
          <div class="mb-6 bg-gray-50 p-4 rounded-lg">
            <h4 class="text-sm font-medium text-gray-700 mb-3">FOLLOW-UP</h4>
            
            <div v-if="!isEditingFollowUp">
              <div class="flex justify-between items-center">
                <div>
                  <div v-if="activeLead?.follow_up_date" class="flex items-center gap-2">
                    <span 
                      class="h-3 w-3 rounded-full" 
                      :class="[
                        isOverdue(activeLead.follow_up_date) ? 'bg-red-600' : 
                        isToday(activeLead.follow_up_date) ? 'bg-red-500' :
                        isUpcoming(activeLead.follow_up_date) ? 'bg-amber-400' :
                        'bg-green-500'
                      ]"
                    ></span>
                    <span 
                      class="text-sm font-medium"
                      :class="[
                        isOverdue(activeLead.follow_up_date) ? 'text-red-700' : 
                        isToday(activeLead.follow_up_date) ? 'text-red-600' :
                        isUpcoming(activeLead.follow_up_date) ? 'text-amber-700' :
                        'text-green-700'
                      ]"
                    >
                      {{ formatFollowUpDateLong(activeLead.follow_up_date) }}
                    </span>
                  </div>
                  <div v-else class="text-gray-500 text-sm">No follow-up scheduled</div>
                </div>
                <div class="flex gap-2">
                  <button 
                    @click="startEditFollowUp" 
                    class="text-sm bg-secondary text-white hover:text-gray-900 px-2 py-1 rounded"
                  >
                    {{ activeLead?.follow_up_date ? 'Edit' : 'Set Date' }}
                  </button>
                  <button 
                    v-if="activeLead?.follow_up_date"
                    @click="clearFollowUpDate" 
                    class="text-sm text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
            
            <div v-else class="space-y-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Select follow-up date</label>
                <input 
                  type="datetime-local" 
                  v-model="followUpDateDraft"
                  class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  min=""
                />
              </div>
              <div class="flex justify-end gap-2">
                <button 
                  @click="cancelEditFollowUp" 
                  class="px-3 py-1.5 text-sm border border-gray-200 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button 
                  @click="saveFollowUpDate" 
                  class="px-3 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-primary/90"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
          
          <!-- Comments section -->
          <div class="mb-6">
            <h4 class="text-sm font-medium text-gray-500 mb-2">COMMENTS</h4>
            <div v-if="leadComments.length === 0" class="text-gray-400 text-sm italic">
              No comments yet
            </div>
            <div v-else class="space-y-4">
              <div 
                v-for="(comment, index) in leadComments" 
                :key="index" 
                class="bg-gray-50 rounded-lg p-3"
              >
                <div class="flex justify-between items-start mb-2">
                  <div class="font-medium">{{ $store.getters['leads/usernameById'](comment.user_id) }}</div>
                  <div class="text-xs text-gray-500">{{ formatCommentDate(comment.created_at) }}</div>
                </div>
                <p class="text-sm text-gray-700">{{ comment.comment }}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div class="p-4 border-t">
          <div class="mb-2">
            <label for="comment" class="block text-sm font-medium text-gray-700 mb-1">Add a comment</label>
            <textarea 
              id="comment"
              v-model="newComment" 
              rows="3" 
              class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="Type your comment here..."
            ></textarea>
          </div>
          <button 
            @click="addComment" 
            class="w-full px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary/90 transition-colors"
            :disabled="!newComment.trim()"
          >
            Add Comment
          </button>
        </div>
      </div>
      
      <!-- Email Generator Slider -->
    <div 
      v-if="emailSliderOpen" 
      class="fixed inset-y-0 right-0 w-full sm:w-1/3 bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-30 overflow-hidden flex flex-col"
      :class="{ 'translate-x-0': emailSliderOpen, 'translate-x-full': !emailSliderOpen }"
    >
      <div class="flex items-center justify-between p-4 border-b">
        <div>
          <h3 class="text-lg font-semibold">Generate Email</h3>
          <p class="text-sm text-gray-500">{{ activeLead?.name }}</p>
        </div>
        <button 
          @click="closeEmailSlider" 
          class="p-2 rounded-full hover:bg-gray-100"
        >
          <span class="text-xl">×</span>
        </button>
      </div>
      
      <div class="flex-1 overflow-y-auto p-4">
        <div class="mb-6">
          <label for="emailConsiderations" class="block text-sm font-medium text-gray-700 mb-2">Additional considerations</label>
          <textarea 
            id="emailConsiderations"
            v-model="emailConsiderations" 
            rows="5" 
            class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
            placeholder="Add any specific points you want to include in the email..."
          ></textarea>
        </div>
        
        <div v-if="generatedEmail.subject" class="mb-6 bg-gray-50 p-4 rounded-lg">
          <div class="flex justify-between items-center mb-2">
            <h4 class="text-sm font-medium text-gray-700">GENERATED SUBJECT</h4>
            <button 
              @click="copyGeneratedEmailSubject" 
              class="text-sm bg-primary text-black hover:text-gray-900 px-2 py-1 rounded flex items-center gap-1"
            >
              <span v-if="emailCopiedSubject">Copied!</span>
              <span v-else>Copy</span>
            </button>
          </div>
          <div class="text-sm whitespace-pre-wrap">{{ generatedEmail.subject }}</div>
        </div>
        <div v-if="generatedEmail.body" class="mb-6 bg-gray-50 p-4 rounded-lg">
          <div class="flex justify-between items-center mb-2">
            <h4 class="text-sm font-medium text-gray-700">GENERATED BODY</h4>
            <button 
              @click="copyGeneratedEmailBody" 
              class="text-sm bg-primary text-black hover:text-gray-900 px-2 py-1 rounded flex items-center gap-1"
            >
              <span v-if="emailCopiedBody">Copied!</span>
              <span v-else>Copy</span>
            </button>
          </div>
          <div class="text-sm whitespace-pre-wrap">{{ generatedEmail.body }}</div>
        </div>
      </div>
      
      <div class="p-4 border-t">
        <button 
          @click="generateEmail" 
          class="w-full px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
          :disabled="isGenerating"
        >
          <span v-if="isGenerating" class="animate-spin">
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
          <span>{{ isGenerating ? 'Generating...' : 'Generate Email' }}</span>
        </button>
      </div>
    </div>
      
      <!-- Overlay when sliders are open -->
      <div 
        v-if="commentsSliderOpen || emailSliderOpen" 
        class="fixed inset-0 bg-black bg-opacity-25 z-20"
        @click="commentsSliderOpen ? closeCommentsSlider() : closeEmailSlider()"
      ></div>
    </div>
  </div>

  <!-- Edit Modal -->
  <div
    v-if="editModalOpen"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
  >
    <div class="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
      <h2 class="text-xl font-semibold mb-4">Edit Lead</h2>
      <div class="space-y-4">
        <input
          v-model="editDraft.name"
          type="text"
          placeholder="Name"
          class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
        <select
          v-model="editDraft.status"
          class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
        >
          <option v-for="s in statusesList" :key="s" :value="s">{{ s }}</option>
        </select>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Follow-up Date</label>
          <div class="flex gap-2">
            <input
              v-model="editDraft.follow_up_date"
              type="date"
              class="flex-1 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            <button 
              @click="editDraft.follow_up_date = ''" 
              class="px-3 py-1 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50"
              :disabled="!editDraft.follow_up_date"
            >
              Clear
            </button>
          </div>
        </div>
        <input
          v-model="editDraft.contact_person"
          type="text"
          placeholder="Contact Person"
          class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
        <input
          v-model="editDraft.phone"
          type="text"
          placeholder="Phone"
          class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
        <input
          v-model="editDraft.email"
          type="email"
          placeholder="Email"
          class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
        <input
          v-model="editDraft.street"
          type="text"
          placeholder="Street"
          class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
        <input
          v-model="editDraft.city"
          type="text"
          placeholder="City"
          class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
        <input
          v-model="editDraft.state"
          type="text"
          placeholder="State"
          class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
        <input
          v-model="editDraft.zip"
          type="text"
          placeholder="Zipcode"
          class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
      </div>
      <div class="mt-6 flex justify-end gap-3">
        <button
          @click="closeEditModal"
          class="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
        >
          Cancel
        </button>
        <button
          @click="saveEdit"
          class="px-4 py-2 rounded-lg bg-primary text-black hover:bg-primary/90"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { useStore } from 'vuex'
  import SideBar from '@/components/SideBar.vue'; 
  import { ref, reactive, computed, onMounted, watch } from 'vue';
  const store = useStore()

  // Table columns
  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'lead_owner', label: 'Lead OWner' },
    { key: 'status', label: 'Status' },
    { key: 'phone', label: 'Phone' },
    { key: 'follow_up_date', label: 'Follow-up' },
  ];
  const statusesList = ['New', 'Contacting','Voicemail', 'Inactive', 'Pricing Shared', 'Samples Sent', 'Ordered', 'Important'];

  // Initial data from store
  const allLeads = computed(() => store.state.leads.all)

  // Filtering and sorting state
  const searchQuery = ref('');
  const statusFilter = ref('');
  const stateFilter = ref('');
  const followUpFilter = ref('');
  const sortBy = ref('follow_up_date');
  const sortDirection = ref('asc');
  const copiedId = ref(null)
  const statusDropdownOpen = ref(null);
  const statusDraft = ref('');
  const editModalOpen = ref(false);
  const isCreating = ref(false)
  const editDraft = reactive({
    id: '',
    name: '',
    status: '',
    phone: '',
    follow_up_date: '',
  });

  // Comments slider state
  const commentsSliderOpen = ref(false);
  const activeLead = ref(null);
  const newComment = ref('');
  const leadComments = computed(() =>
      activeLead.value
          ? store.getters['leads/commentsFor'](activeLead.value.id)
          : []
  );

  // Email generator state
  const emailSliderOpen = ref(false);
  const emailConsiderations = ref('');
  const generatedEmail = reactive({subject: '',body: ''});
  const isGenerating = ref(false);
  const emailCopiedSubject = ref(false);
  const emailCopiedBody = ref(false);
  
  // Follow-up date state
  const isEditingFollowUp = ref(false);
  const followUpDateDraft = ref('');
  
  // Pagination state
  const currentPage = ref(1);
  const itemsPerPage = ref(10);

  const getInitials = (name = '') => {
  return name
    .split(' ')
    .map(part => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

 const getAvatarColor = (id) =>  {
  const colors = [
    '#FFB3BA', // soft pink
    '#FFDFBA', // peach
    '#FFFFBA', // light yellow
    '#BAFFC9', // mint
    '#BAE1FF', // baby blue
    '#E5BAFF', // lavender
    '#D5F4E6', // pale aqua
    '#FFCCE5', // light rose
    '#C5FAD5', // seafoam
    '#FFF2CC'  // cream
  ];
    return colors[id % colors.length] || '#95a5a6';
  }

// parse operating_hours array from meta JSON
function parseHours(meta) {
  try {
    const data = JSON.parse(meta || '{}');
    let { operating_hours: hrs } = data;
    if (!hrs) return [];
    // if it’s an object (day→hours), convert to array
    if (!Array.isArray(hrs) && typeof hrs === 'object') {
      hrs = Object.entries(hrs).map(([day, hours]) => ({ day, hours }));
    }
    return Array.isArray(hrs) ? hrs : [];
  } catch {
    return [];
  }
}

// check if store is open right now
function isOpenNowFancy(hourRange, day, timezone = 'America/Los_Angeles') {
  if (!hourRange || !day) return false;
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: timezone }));
  const today = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  if (day.toLowerCase() !== today) return false;

  const minutes = now.getHours() * 60 + now.getMinutes();
  const [openStr, closeStr] = hourRange.replace(/[–—‒−]/g, '-').split('-').map(s => s.trim());

  const toMin = str => {
    if(!str) return null;
    const m = str.match(/(\d{1,2})(?::(\d{2}))?\s?(AM|PM)/i);
    if (!m) return null;
    let h = +m[1], min = +m[2]||0;
    if (m[3].toUpperCase()==='PM' && h<12) h+=12;
    if (m[3].toUpperCase()==='AM' && h===12) h=0;
    return h*60 + min;
  };

  const o = toMin(openStr), c = toMin(closeStr);
  if (o == null || c == null) return false;

  // handle overnight (close next day)
  if (c <= o) {
    return minutes >= o || minutes <= c;
  }
  // normal same-day range
  return minutes >= o && minutes <= c;
}


// get today’s hours string
function getTodayHours(meta) {
  const entry = parseHours(meta).find(
    h => h.day.toLowerCase() === todayName.toLowerCase()
  );
  return entry?.hours || '';
}


// reactive helper for today's name (for reactivity if you need it)
const todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });


  // Date helper functions
  const today = computed(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  });
  
  const isOverdue = (date) => {
    if (!date) return false;
    return new Date(date).getTime() < today.value;
  };
  
  const isToday = (date) => {
    if (!date) return false;
    return new Date(date).getTime() === today.value;
  };
  
  const isUpcoming = (date) => {
    if (!date) return false;
    const dateTime = new Date(date).getTime();
    const weekFromNow = today.value + (7 * 24 * 60 * 60 * 1000);
    return dateTime > today.value && dateTime <= weekFromNow;
  };
  
  const formatFollowUpDate = (date) => {
  if (!date) return '';
  const options = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };
  return new Date(date).toLocaleString(undefined, options);
};

  
  const formatFollowUpDateLong = (date) => {
  if (!date) return '';

  const followUpDate = new Date(date); // assume UTC ISO from server
  const now = new Date(); // use browser's local time
  const diffTime = followUpDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const options = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };

  const formattedDateTime = followUpDate.toLocaleString(undefined, options); // browser auto uses its own tz

  if (diffDays < 0) {
    return `Overdue: ${formattedDateTime} (${Math.abs(diffDays)} days ago)`;
  } else if (diffDays === 0) {
    return `Today: ${formattedDateTime}`;
  } else if (diffDays === 1) {
    return `Tomorrow: ${formattedDateTime}`;
  } else {
    return `${formattedDateTime} (in ${diffDays} days)`;
  }
};



  // Follow-up functions
  function startEditFollowUp() {
    followUpDateDraft.value = activeLead.value?.follow_up_date || '';
    isEditingFollowUp.value = true;
  }
  
  function cancelEditFollowUp() {
    isEditingFollowUp.value = false;
    followUpDateDraft.value = '';
  }
  
  
  async function saveFollowUpDate() {
  if (!activeLead.value || !followUpDateDraft.value) return;

  // Convert local datetime from picker to proper UTC string
  const localDate = new Date(followUpDateDraft.value); // already local
  const isoUTC = localDate.toISOString(); // safely shifts to UTC

  // Send to backend
  const updated = await store.dispatch('leads/updateLead', {
    id: activeLead.value.id,
    updates: { follow_up_date: isoUTC }
  });

  // Update frontend
  activeLead.value.follow_up_date = updated.follow_up_date;
  isEditingFollowUp.value = false;
}



  async function clearFollowUpDate() {
      if (!activeLead.value) return

      const updated = await store.dispatch('leads/updateLead', {
          id:      activeLead.value.id,
          updates: { follow_up_date: null }
      })

      activeLead.value.follow_up_date = updated.follow_up_date  // will be null
  }


  // Comments functions
  function openCommentsSlider(lead) {
    activeLead.value = lead;
    commentsSliderOpen.value = true;
    newComment.value = '';
    store.dispatch('leads/fetchComments', lead.id);
  }

  function closeCommentsSlider() {
    commentsSliderOpen.value = false;
    isEditingFollowUp.value = false;
    setTimeout(() => {
      activeLead.value = null;
    }, 300); // Wait for transition to complete
  }

  // Email generator functions
  function openEmailSlider(lead) {
    activeLead.value = lead;
    emailSliderOpen.value = true;
    emailConsiderations.value = '';
    generatedEmail.body = '';
    generatedEmail.subject = '';
    emailCopiedSubject.value = false;
    emailCopiedBody.value = false;

  }
  
  function closeEmailSlider() {
    emailSliderOpen.value = false;
    setTimeout(() => {
      if (!commentsSliderOpen.value) {
        activeLead.value = null;
      }
    }, 300); // Wait for transition to complete
  }
  
  async function generateEmail() {
  if (!activeLead.value) return;

  isGenerating.value = true;
  generatedEmail.body = '';
  generatedEmail.subject = '';

  try {
    const response = await store.dispatch('leads/generateEmail', {
      lead: activeLead.value,
      considerations: emailConsiderations.value
    });
    generatedEmail.subject = response.subject;
    generatedEmail.body = response.body;
  } catch (error) {
    console.error('Error generating email:', error);
  } finally {
    isGenerating.value = false;
  }
}
  function copyGeneratedEmailSubject() {
      if (!generatedEmail.body) return;
      
      navigator.clipboard.writeText(generatedEmail.subject).then(() => {
        emailCopiedSubject.value = true;
        setTimeout(() => {
          emailCopiedSubject.value = false;
        }, 2000);
      });
    }
  
  function copyGeneratedEmailBody() {
    if (!generatedEmail.body) return;
    
    navigator.clipboard.writeText(generatedEmail.body).then(() => {
      emailCopiedBody.value = true;
      setTimeout(() => {
        emailCopiedBody.value = false;
      }, 2000);
    });
  }

  async function addComment() {
    if (!newComment.value.trim()) return;
    await store.dispatch('leads/addComment', {
      leadId: activeLead.value.id,
      text: newComment.value.trim()
    });
    newComment.value = '';
  }

  function formatCommentDate(date) {
    // Format date nicely
    return new Date(date).toLocaleString();
  }

  function openAddModal() {
    isCreating.value = true;
    Object.assign(editDraft, { 
      id: '', 
      name: '', 
      status: 'New', 
      phone: '', 
      email: '', 
      street: '', 
      city: '', 
      state: '', 
      zip: '',
      follow_up_date: '' 
    });
    editModalOpen.value = true;
  }
  
  function openEditModal(lead) {
    isCreating.value = false;
    Object.assign(editDraft, { ...lead });
    editModalOpen.value = true;
  }

  function closeEditModal() {
    editModalOpen.value = false;
  }

  async function saveEdit() {
    const { id, ...fields } = editDraft;
    if (isCreating.value) {
      await store.dispatch('leads/createLead', fields);
    } else {
      await updateLead({ id }, fields);
    }
    editModalOpen.value = false;
  }
  
  const updateLead = async (activeLead, updates) => {
    // close any open dropdown
    statusDropdownOpen.value = null;
    try {
      // dispatch to your Vuex store; action does the API call
      await store.dispatch('leads/updateLead', {
        id: activeLead.id,
        updates
      });
    } catch (err) {
      console.error('Lead update failed', err);
    }
  };

  // Computed properties for filtering and sorting
  const filteredLeads = computed(() => {
    const q = searchQuery.value.toLowerCase();
    return allLeads.value
      .filter(lead => {
        // Text search filter
        const name = lead.name?.toLowerCase() || '';
        const email = lead.email?.toLowerCase() || '';
        const company = lead.company?.toLowerCase() || '';
        const matchesSearch = name.includes(q) || email.includes(q) || company.includes(q);
        
        // Status filter
        const matchesStatus = !statusFilter.value || lead.status === statusFilter.value;

        // State filter
        const matchesState = !stateFilter.value || (lead.state && lead.state.toUpperCase() === stateFilter.value);
        
        // Follow-up filter
        let matchesFollowUp = true;
        if (followUpFilter.value === 'today') {
          matchesFollowUp = isToday(lead.follow_up_date);
        } else if (followUpFilter.value === 'overdue') {
          matchesFollowUp = isOverdue(lead.follow_up_date);
        } else if (followUpFilter.value === 'upcoming') {
          matchesFollowUp = isUpcoming(lead.follow_up_date);
        } else if (followUpFilter.value === 'scheduled') {
          matchesFollowUp = !!lead.follow_up_date;
        } else if (followUpFilter.value === 'none') {
          matchesFollowUp = !lead.follow_up_date;
        }
        
        return matchesSearch && matchesStatus && matchesFollowUp && matchesState;
      })
      .sort((a, b) => {
        let cmp = 0;
        if (sortBy.value === 'name') {
          cmp = (a.name || '').localeCompare(b.name || '');
        } else if (sortBy.value === 'company') {
          cmp = (a.company || '').localeCompare(b.company || '');
        } else if (sortBy.value === 'date') {
          cmp = new Date(a.date || 0) - new Date(b.date || 0);
        } else if (sortBy.value === 'value') {
          cmp = (a.value || 0) - (b.value || 0);
        } else if (sortBy.value === 'follow_up_date') {
          // Sort null values to the end
          if (!a.follow_up_date && !b.follow_up_date) cmp = 0;
          else if (!a.follow_up_date) cmp = 1;
          else if (!b.follow_up_date) cmp = -1;
          else cmp = new Date(a.follow_up_date) - new Date(b.follow_up_date);
        }
        return sortDirection.value === 'asc' ? cmp : -cmp;
      });
  });

  // Pagination computed properties
  const totalPages = computed(() => {
    return Math.max(1, Math.ceil(filteredLeads.value.length / itemsPerPage.value));
  });

  const startIndex = computed(() => {
    return (currentPage.value - 1) * itemsPerPage.value;
  });

  const endIndex = computed(() => {
    return startIndex.value + itemsPerPage.value;
  });

  const paginatedLeads = computed(() => {
    return filteredLeads.value.slice(startIndex.value, endIndex.value);
  });

  // Display page numbers with ellipsis for large page counts
  const displayedPageNumbers = computed(() => {
    const total = totalPages.value;
    const current = currentPage.value;
    const delta = 1; // Number of pages to show before and after current page
    
    if (total <= 7) {
      // If we have 7 or fewer pages, show all
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    
    // Always include first and last page
    const pages = [1];
    
    // Calculate range around current page
    const rangeStart = Math.max(2, current - delta);
    const rangeEnd = Math.min(total - 1, current + delta);
    
    // Add ellipsis if needed before current range
    if (rangeStart > 2) {
      pages.push('...');
    }
    
    // Add pages around current page
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }
    
    // Add ellipsis if needed after current range
    if (rangeEnd < total - 1) {
      pages.push('...');
    }
    
    // Add last page
    if (total > 1) {
      pages.push(total);
    }
    
    return pages;
  });

  // Methods
  const handleSort = (column) => {
    if (sortBy.value === column) {
      sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
    } else {
      sortBy.value = column;
      sortDirection.value = 'asc';
    }
  };

  const getStatusClass = (status) => {
    const classes = {
      'New': 'bg-lime-100 text-lime-800',
      'Contacting': 'bg-green-100 text-green-800',
      'Voicemail': 'bg-teal-100 text-teal-800',
      'Inactive': 'bg-gray-100 text-gray-800',
      'Pricing Shared': 'bg-blue-100 text-blue-800',
      'Samples Sent': 'bg-purple-100 text-purple-800',
      'Ordered': 'bg-orange-100 text-orange-800',
      'Important': 'bg-red-100 text-red-800'

    };
    
    return classes[status] || 'bg-gray-100 text-gray-800';
  };

  const loadFromStore = () => {
    store.dispatch('leads/fetchLeads', {
      search: searchQuery.value,
      status: statusFilter.value,
      sortBy: sortBy.value,
      sortDir: sortDirection.value
    });
  };

  function copyPhone(phone, id) {
    navigator.clipboard.writeText(phone).then(() => {
      copiedId.value = id;
      setTimeout(() => (copiedId.value = null), 1500);
    });
  }
  
  function openStatusDropdown(lead) {
    statusDropdownOpen.value = lead.id;
    statusDraft.value = lead.status;
  }
  
  // Reset to first page when filters change
  watch([searchQuery, statusFilter, stateFilter, followUpFilter, sortBy, sortDirection], () => {
    currentPage.value = 1;
    loadFromStore();
  }, { immediate: false });

  // Lifecycle hooks
  onMounted(() => {
    store.dispatch('leads/fetchUserMap');
    loadFromStore();
  });

  const availableStates = computed(() => {
    const states = new Set();
    allLeads.value.forEach(lead => {
      if (lead.state) {
        const s = lead.state.trim().toUpperCase();
        if (s.length === 2) {
          states.add(s);
        }
      }
    });
    return Array.from(states).sort();
  });
</script>

<style>
:root {
  --color-primary: #6366f1;
  --color-primary-hover: #4f46e5;
}

.bg-primary {
  background-color: var(--color-primary);
}

.text-primary {
  color: var(--color-primary);
}

.border-primary {
  border-color: var(--color-primary);
}

.hover\:bg-primary\/90:hover {
  background-color: var(--color-primary-hover);
}

.focus\:ring-primary\/20:focus {
  --tw-ring-color: color-mix(in srgb, var(--color-primary) 20%, transparent);
}

.focus\:border-primary:focus {
  border-color: var(--color-primary);
}
</style>
